import {Dialog, openDialog} from 'platform/components';
import {Box, Text, VStack} from 'platform/foundation';

import {useCallback, useRef, useState} from 'react';

import {useLazyGetConfigurationDetailQuery} from '@dms/api/new-vehicle';
import i18n from '@dms/i18n';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {useOnMount} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

import {getOrderConfigurationDialogId} from '../OrderConfigurationDialog/getOrderConfigurationDialogId';
import {
  OrderConfigurationDialog,
  type SaleVehicleReplacement,
} from '../OrderConfigurationDialog/OrderConfigurationDialog';

export type SelectConfigurationDialogProps = {
  businessCaseId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Set when the pick replaces the Business Case's current sale vehicle rather than filling an empty slot. */
  replacement?: SaleVehicleReplacement;
  onDone?: VoidFunction;
};

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

export function SelectConfigurationDialog(props: SelectConfigurationDialogProps) {
  const [getDetail] = useLazyGetConfigurationDetailQuery();
  const [selectedId, setSelectedId] = useState<string>();
  const isMounted = useRef(true);
  const detailRequestSequence = useRef(0);
  useOnMount(
    () => {
      isMounted.current = true;
    },
    () => {
      isMounted.current = false;
    }
  );

  // A configuration can only adopt a Business Case while it has neither one nor a vehicle:
  // committing is what materialises the order, so anything already bound is not selectable.
  const queryModifier = useCallback(
    (filter: QueryFilterObject) => ({
      ...filter,
      businessCaseUnassigned: true,
      vehicleUnassigned: true,
    }),
    []
  );

  const pickConfiguration = (configurationId: string) => {
    const sequence = detailRequestSequence.current + 1;
    detailRequestSequence.current = sequence;
    void getDetail({configurationId}, true)
      .unwrap()
      .then((configuration) => {
        if (!isMounted.current || detailRequestSequence.current !== sequence) {
          return;
        }
        props.onClose();
        const dialogId = getOrderConfigurationDialogId(configurationId);
        openDialog(
          <OrderConfigurationDialog
            configuration={configuration}
            targetBusinessCaseId={props.businessCaseId}
            replacement={props.replacement}
            dialogId={dialogId}
            onDone={props.onDone}
          />,
          {
            id: dialogId,
            title: t('entity.newVehicle.order.title', 'Order configuration'),
            size: 'large',
          }
        );
      })
      .catch(handleApiError);
  };

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="large"
      title={t(
        'entity.newVehicle.actions.selectExistingConfiguration',
        'Select existing configuration'
      )}
      data-testid="business-case-select-new-vehicle-configuration"
      buttons={[
        {
          title: i18n.t('general.actions.cancel'),
          variant: 'secondary',
          onClick: props.onClose,
        },
        {
          title: i18n.t('general.labels.select'),
          variant: 'primary',
          isDisabled: !selectedId,
          onClick: () => {
            if (selectedId) {
              pickConfiguration(selectedId);
            }
          },
        },
      ]}
    >
      <VStack spacing={4}>
        <Text color="secondary">
          {t(
            'entity.newVehicle.selectConfiguration.intro',
            'Only configurations without a Business Case or vehicle can be used. Ordering one adds it to this Business Case.'
          )}
        </Text>
        <Box height="60vh">
          <DmsDataGrid
            gridCode="business-case-add-new-vehicle-configuration"
            presetInstanceId={`business-case-add-new-vehicle-configuration-${props.businessCaseId}`}
            queryModifier={queryModifier}
            onRowSelectionChange={(rows) => setSelectedId(rows[0]?.id)}
            emptyState={{
              headline: t(
                'entity.newVehicle.selectConfiguration.empty',
                'No unassigned configurations'
              ),
              subheadline: t(
                'entity.newVehicle.selectConfiguration.emptySubheadline',
                'Configure a new vehicle for this Business Case instead'
              ),
            }}
          />
        </Box>
      </VStack>
    </Dialog>
  );
}
